import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays, Wallet, Ticket, CheckCircle2, AlertTriangle, MapPin, Users,
} from "lucide-react";
import {
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { useData } from "@/context/DataContext";
import { PageHeader, KpiCard, ProgressBar, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { euro, formatDate, daysUntil, eventUrgency, progressBarClass, CHART_COLORS } from "@/lib/format";
import client from "@/lib/api";
import { toast } from "sonner";

const INSURABLE = ["Défaillance d'un prestataire", "Force majeure", "Dommage corporel", "Dommage matériel", "Responsabilité civile"];
const INTERNAL = ["Panne technique", "Incident de sécurité", "Intoxication alimentaire", "Surpopulation", "Retard majeur", "Litige avec un prestataire", "Autre"];

export default function Dashboard() {
  const navigate = useNavigate();
  const { events, tasks, guests, expenses, setSelectedEventId } = useData();
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [ticketRevenue, setTicketRevenue] = useState(0);

  React.useEffect(() => {
    client.get("/stats/ticket-revenue").then(({ data }) => setTicketRevenue(data.revenue || 0)).catch(() => {});
  }, []);

  const stats = useMemo(() => {
    const now = new Date();
    const upcoming = events.filter((e) => daysUntil(e.startDate) > 0).length;
    const ongoing = events.filter((e) => {
      const s = daysUntil(e.startDate), en = daysUntil(e.endDate || e.startDate);
      return s <= 0 && en >= 0;
    }).length;
    const past = events.filter((e) => daysUntil(e.endDate || e.startDate) < 0).length;
    const totalBudget = events.reduce((a, e) => a + Number(e.budget || 0), 0);
    const spent = expenses.reduce((a, x) => a + Number(x.amount || 0), 0);
    const revenue = guests.filter((g) => g.status === "confirmed").reduce((a, g) => a + Number(g.ticketPrice || 0), 0);
    const eventIds = new Set(events.map((e) => e.id));
    const validTasks = tasks.filter((t) => !t.eventId || eventIds.has(t.eventId));
    const done = validTasks.filter((t) => t.status === "done").length;
    const completion = validTasks.length ? Math.round((done / validTasks.length) * 100) : 0;
    const budgetUsage = totalBudget ? Math.round((spent / totalBudget) * 100) : 0;
    return { upcoming, ongoing, past, totalBudget, spent, revenue, done, completion, budgetUsage, total: validTasks.length };
  }, [events, tasks, guests, expenses]);

  const budgetChart = events.map((e) => ({
    name: e.name.length > 12 ? e.name.slice(0, 12) + "…" : e.name,
    Budget: Number(e.budget || 0),
    Dépenses: expenses.filter((x) => x.eventId === e.id).reduce((a, x) => a + Number(x.amount || 0), 0),
  }));

  const monthChart = useMemo(() => {
    const months = [];
    const base = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(base.getFullYear(), base.getMonth() + i, 1);
      const label = d.toLocaleDateString("fr-FR", { month: "short" });
      const count = events.filter((e) => {
        const ed = new Date(e.startDate);
        return ed.getMonth() === d.getMonth() && ed.getFullYear() === d.getFullYear();
      }).length;
      months.push({ month: label, Événements: count });
    }
    return months;
  }, [events]);

  const typeChart = useMemo(() => {
    const map = {};
    events.forEach((e) => { map[e.type || "Autre"] = (map[e.type || "Autre"] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [events]);

  const topCities = useMemo(() => {
    const map = {};
    events.forEach((e) => { if (e.city) map[e.city] = (map[e.city] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [events]);

  const upcomingList = [...events]
    .filter((e) => daysUntil(e.startDate) >= 0)
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
    .slice(0, 5);

  if (!events.length) {
    return (
      <>
        <PageHeader title="Tableau de bord"
          actions={null} />
        <EmptyState title="Bienvenue sur JustPlanIt 🎉" description="Créez votre premier événement pour voir vos statistiques ici."
          action={<Button onClick={() => navigate("/create-event")} data-testid="dash-create-event">Créer un événement</Button>} />
        <IncidentModal open={incidentOpen} onClose={() => setIncidentOpen(false)} events={events} />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Tableau de bord" subtitle="Vue d'ensemble de tous vos événements"
        actions={null} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="kpi-events" icon={CalendarDays} tint="indigo" label="Événements total" value={events.length}
          sub={`${stats.upcoming} à venir · ${stats.ongoing} en cours · ${stats.past} passés`} onClick={() => navigate("/events")} />
        <KpiCard testid="kpi-budget" icon={Wallet} tint="blue" label="Budget géré" value={euro(stats.totalBudget)}
          sub={`${stats.budgetUsage}% utilisé · ${euro(stats.spent)} dépensé`} onClick={() => navigate("/budget")} />
        <KpiCard testid="kpi-revenue" icon={Ticket} tint="violet" label="Revenus billets" value={euro(stats.revenue + ticketRevenue)}
          sub={`${euro(ticketRevenue)} via billetterie en ligne`} onClick={() => navigate("/invites")} />
        <KpiCard testid="kpi-tasks" icon={CheckCircle2} tint="emerald" label="Complétion tâches" value={`${stats.completion}%`}
          sub={`${stats.done}/${stats.total} complétées`} onClick={() => navigate("/tasks")} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-card rounded-xl border border-border p-5">
          <div className="flex justify-between text-sm mb-2"><span className="font-medium">Complétion des tâches</span><span className="font-semibold">{stats.completion}%</span></div>
          <ProgressBar pct={stats.completion} />
        </div>
        <div className="bg-card rounded-xl border border-border p-5">
          <div className="flex justify-between text-sm mb-2"><span className="font-medium">Utilisation du budget global</span><span className="font-semibold">{stats.budgetUsage}%</span></div>
          <ProgressBar pct={stats.budgetUsage} />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="font-display font-semibold mb-4">Budget vs Dépenses</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={budgetChart}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => euro(v)} /><Legend />
              <Bar dataKey="Budget" fill="#6366f1" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Dépenses" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="font-display font-semibold mb-4">Événements — 6 prochains mois</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={monthChart}>
              <defs><linearGradient id="ev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} /><stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip /><Area type="monotone" dataKey="Événements" stroke="#8b5cf6" fill="url(#ev)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="font-display font-semibold mb-4">Répartition par type</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={typeChart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                {typeChart.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="font-display font-semibold mb-4">Top 5 villes</h3>
          {topCities.length ? (
            <ol className="space-y-3">
              {topCities.map(([city, count], i) => (
                <li key={city} className="flex items-center gap-3" data-testid={`top-city-${i}`}>
                  <span className="h-7 w-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center text-sm font-bold">{i + 1}</span>
                  <span className="flex-1 font-medium">{city}</span>
                  <span className="text-sm text-muted-foreground">{count} événement{count > 1 ? "s" : ""}</span>
                </li>
              ))}
            </ol>
          ) : <p className="text-sm text-muted-foreground">Aucune donnée.</p>}
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border p-5">
        <h3 className="font-display font-semibold mb-4">Événements à venir</h3>
        <div className="space-y-2">
          {upcomingList.length ? upcomingList.map((e) => {
            const u = eventUrgency(e.startDate);
            return (
              <div key={e.id} onClick={() => { setSelectedEventId(e.id); navigate("/events"); }} className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted transition-colors cursor-pointer" data-testid={`upcoming-${e.id}`}>
                <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: e.color || "#8b5cf6" }}><CalendarDays className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{e.name}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-3">
                    <span>{formatDate(e.startDate)}</span>
                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{e.city || "—"}</span>
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" />{e.maxParticipants || 0}</span>
                  </p>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${u.cls}`}>{u.label}</span>
              </div>
            );
          }) : <p className="text-sm text-muted-foreground">Aucun événement à venir.</p>}
        </div>
      </div>

      <IncidentModal open={incidentOpen} onClose={() => setIncidentOpen(false)} events={events} />
    </>
  );
}

function IncidentModal({ open, onClose, events }) {
  const [eventId, setEventId] = useState("");
  const [type, setType] = useState("");
  const [desc, setDesc] = useState("");
  const [sent, setSent] = useState(null);
  const [loading, setLoading] = useState(false);

  const category = INSURABLE.includes(type) ? "Assurable (AXA)" : type ? "Alerte interne" : "";

  const send = async () => {
    if (!eventId || !type || !desc) { toast.error("Complétez tous les champs"); return; }
    setLoading(true);
    try {
      const ev = events.find((e) => e.id === eventId);
      await client.post("/incident", { eventId, eventName: ev?.name || "", type, category, description: desc });
      setSent(category);
    } catch { toast.error("Erreur d'envoi"); } finally { setLoading(false); }
  };

  const reset = () => { setEventId(""); setType(""); setDesc(""); setSent(null); onClose(); };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Signaler un incident</DialogTitle></DialogHeader>
        {sent ? (
          <div className="py-6 text-center" data-testid="incident-confirmation">
            {sent.includes("AXA") ? (
              <>
                <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                <h3 className="font-display font-semibold text-lg">Déclaration transmise à AXA</h3>
                <p className="text-sm text-muted-foreground mt-1">Un gestionnaire de sinistre vous contactera sous 48h. Une copie a été envoyée par email.</p>
              </>
            ) : (
              <>
                <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
                <h3 className="font-display font-semibold text-lg">Alerte interne enregistrée</h3>
                <p className="text-sm text-muted-foreground mt-1">Votre équipe JustPlanIt a été notifiée et traitera l'incident rapidement.</p>
              </>
            )}
            <Button className="mt-5" onClick={reset} data-testid="incident-close">Fermer</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Événement concerné</label>
              <Select value={eventId} onValueChange={setEventId}>
                <SelectTrigger data-testid="incident-event"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Type d'incident</label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="incident-type"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  <div className="px-2 py-1 text-xs font-bold text-emerald-600">✅ Assurable (AXA)</div>
                  {INSURABLE.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  <div className="px-2 py-1 text-xs font-bold text-amber-600 mt-1">⚠️ Alerte interne</div>
                  {INTERNAL.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              {category && <p className="text-xs mt-1 text-muted-foreground">Catégorie : <span className="font-semibold">{category}</span></p>}
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Description</label>
              <Textarea rows={4} value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="incident-description" placeholder="Décrivez l'incident..." />
            </div>
            <Button className="w-full" onClick={send} disabled={loading} data-testid="incident-send">{loading ? "Envoi..." : "Envoyer le signalement"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
